// React Query hooks for skip-job and skip-enquiry attachments.
//
// Both surfaces share the same DTO shape (presigned URL + metadata).
// The job endpoints have been around since Stage 5; the enquiry
// endpoints landed in Skip Module v2 (migration 102).
//
// Each parent kind (job | enquiry) gets its own list / upload / delete
// hook; the shared kind discriminates which endpoint base to use.

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';

import { apiClient } from '../../../api/client';
import { endpoints } from '../../../api/endpoints';
import { queryKeys } from '../../../api/queryKeys';
import type { SkipEnquiryAttachment, SkipJobAttachment } from '../../../types/api';
import { compressFilesForUpload } from '../../../utils/compressForUpload';

type AttachmentKind = 'job' | 'enquiry';

interface ListResponseJob {
  attachments: SkipJobAttachment[];
}
interface ListResponseEnquiry {
  attachments: SkipEnquiryAttachment[];
}

function listEndpoint(kind: AttachmentKind, encId: string): string {
  return kind === 'job'
    ? endpoints.skipJobs.attachments(encId)
    : endpoints.skipEnquiries.attachments(encId);
}

function singleEndpoint(
  kind: AttachmentKind,
  encId: string,
  attachmentEncId: string,
): string {
  return kind === 'job'
    ? endpoints.skipJobs.attachment(encId, attachmentEncId)
    : endpoints.skipEnquiries.attachment(encId, attachmentEncId);
}

function listKey(kind: AttachmentKind, encId: string) {
  return kind === 'job'
    ? queryKeys.skipJobs.attachments(encId)
    : queryKeys.skipEnquiries.attachments(encId);
}

// -------- JOB --------

export function useSkipJobAttachments(
  encId: string | null | undefined,
): UseQueryResult<ListResponseJob> {
  return useQuery<ListResponseJob>({
    queryKey: queryKeys.skipJobs.attachments(encId ?? ''),
    queryFn: async () => {
      if (!encId) throw new Error('useSkipJobAttachments called without enc_id');
      const res = await apiClient.get<ListResponseJob>(
        endpoints.skipJobs.attachments(encId),
      );
      return res.data;
    },
    enabled: !!encId,
    // Presigned URLs expire in 1h; refetch on focus keeps them live.
    staleTime: 30 * 60_000,
    refetchOnWindowFocus: true,
  });
}

export function useSkipEnquiryAttachments(
  encId: string | null | undefined,
): UseQueryResult<ListResponseEnquiry> {
  return useQuery<ListResponseEnquiry>({
    queryKey: queryKeys.skipEnquiries.attachments(encId ?? ''),
    queryFn: async () => {
      if (!encId) throw new Error('useSkipEnquiryAttachments called without enc_id');
      const res = await apiClient.get<ListResponseEnquiry>(
        endpoints.skipEnquiries.attachments(encId),
      );
      return res.data;
    },
    enabled: !!encId,
    staleTime: 30 * 60_000,
    refetchOnWindowFocus: true,
  });
}

// -------- UPLOAD --------

/**
 * Upload one or more files to either parent kind. Sends multipart
 * FormData under the `attachments` field name (server: multer
 * `.array('attachments', N)`). Server validates magic-byte mime and
 * caps per-file size at MAX_UPLOAD_BYTES; reject paths surface as
 * the standard ApiError envelope.
 */
function useUploadSkipAttachments(kind: AttachmentKind, encId: string) {
  const qc = useQueryClient();
  return useMutation<{ attachments: SkipJobAttachment[] | SkipEnquiryAttachment[] }, Error, FileList | File[]>({
    mutationFn: async (files) => {
      const arr = Array.from(files);
      if (arr.length === 0) throw new Error('No files selected');
      // Centralised pre-upload compression — image files are
      // resized + re-encoded; PDFs pass through untouched. Saves
      // bandwidth and keeps server-side storage costs bounded.
      const compressed = await compressFilesForUpload(arr);
      const fd = new FormData();
      for (const f of compressed) fd.append('attachments', f, f.name);
      const url = listEndpoint(kind, encId);
      // The shared axios instance defaults Content-Type to
      // application/json — when we send FormData, that default
      // would stop the browser from generating the multipart
      // boundary and the server would reject as 400. Setting the
      // header to multipart/form-data here makes axios 1.x accept
      // it AND auto-append `; boundary=...` for FormData bodies.
      const res = await apiClient.post<
        { attachments: SkipJobAttachment[] | SkipEnquiryAttachment[] },
        FormData
      >(url, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: listKey(kind, encId) });
    },
  });
}

export function useUploadSkipJobAttachments(encId: string) {
  return useUploadSkipAttachments('job', encId);
}

export function useUploadSkipEnquiryAttachments(encId: string) {
  return useUploadSkipAttachments('enquiry', encId);
}

// -------- DELETE --------

function useDeleteSkipAttachment(kind: AttachmentKind, encId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, { attachmentEncId: string }>({
    mutationFn: async ({ attachmentEncId }) => {
      await apiClient.delete(singleEndpoint(kind, encId, attachmentEncId));
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: listKey(kind, encId) });
    },
  });
}

export function useDeleteSkipJobAttachment(encId: string) {
  return useDeleteSkipAttachment('job', encId);
}

export function useDeleteSkipEnquiryAttachment(encId: string) {
  return useDeleteSkipAttachment('enquiry', encId);
}
